import { useEffect, useRef, useState } from "react";

import { useParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useCalibrationCertificateViewModel } from "../../../viewmodels/operation/calibrationcertificate/useCalibrationCertificateViewModel";

import ReportHeader from "../../../components/reports/ReportHeader";

import ReportFooter from "../../../components/reports/ReportFooter";

import EditCalibrationCertificateForm from "./EditCalibrationCertificateForm";

import { generateCalibrationCertificatePdf } from "./CalibrationCertificatePdf";

const ViewCalibrationCertificate = () => {
  const { id } = useParams();

  const printRef = useRef<any>(null);

  const { getCalibrationCertificateById } =
    useCalibrationCertificateViewModel();

  const [certificate, setCertificate] = useState<any>(null);

  const [showEditModal, setShowEditModal] = useState(false);

  // ================= LOAD =================
  const loadData = async () => {
    if (!id) return;

    const data = await getCalibrationCertificateById(id);

    if (data) {
      setCertificate(data);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // ================= PRINT =================
  const handlePrint = () => {
    window.print();
  };

  const handleSavePdf = () => {
    if (!certificate) return;

    generateCalibrationCertificatePdf([certificate]);
  };

  if (!certificate) return null;

  return (
    <DashboardLayout>
      <style>
        {`
            @media print {

              body * {
                visibility: hidden;
              }

              #print-area,
              #print-area * {
                visibility: visible;
              }

              #print-area {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                background: white;
              }

              @page {
                size: A4;
                margin: 4mm;
              }

            }
          `}
      </style>

      <div className="p-4">
        <div className="flex justify-end gap-3 mb-4">
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-yellow-600 text-white px-5 py-3 rounded-2xl"
          >
            Edit
          </button>

          <button
            onClick={handlePrint}
            className="bg-primary text-white px-5 py-3 rounded-2xl"
          >
            Print
          </button>
          <button
            onClick={handleSavePdf}
            className="bg-green-600 text-white px-5 py-3 rounded-2xl"
          >
            Save PDF
          </button>
        </div>

        <div
          id="print-area"
          ref={printRef}
          className="bg-white text-black p-3 max-w-[820px] mx-auto text-[12px] leading-[1] min-h-[1085px] flex flex-col"
        >
          {/* HEADER */}
          <ReportHeader />

          {/* TITLE */}
          <div className="text-center font-bold text-[18px] underline mt-2 mb-4">
            TEST / CALIBRATION CERTIFICATE
          </div>

          {/* CUSTOMER DETAILS */}
          <table className="w-full border border-black mb-4">
            <tbody>
              <tr>
                <td className="border border-black p-[4px] font-bold w-[220px]">
                  Customer Name
                </td>

                <td className="border border-black p-[10px]">
                  {certificate.customerName}
                </td>
              </tr>

              <tr>
                <td className="border border-black p-[4px] font-bold">
                  Address
                </td>

                <td className="border border-black p-[10px]">
                  {certificate.customerAddress}
                </td>
              </tr>

              <tr>
                <td className="border border-black p-[4px] font-bold">
                  Scale Location
                </td>

                <td className="border border-black p-[10px]">
                  {certificate.scaleLocation}
                </td>
              </tr>

              <tr>
                <td className="border border-black p-[4px] font-bold">
                  Contact Name
                </td>

                <td className="border border-black p-[10px]">
                  {certificate.contactPerson}
                </td>
              </tr>
            </tbody>
          </table>

          {/* SCALE DETAILS */}
          <table className="w-full border border-black mb-4 text-center">
            <thead>
              <tr className="font-bold">
                <td className="border border-black p-[3px]">Report No.</td>

                <td className="border border-black p-[3px]">Make & Model</td>

                <td className="border border-black p-[3px]">Capacity</td>

                <td className="border border-black p-[3px]">S/Number</td>

                <td className="border border-black p-[3px]">ID</td>

                <td className="border border-black p-[3px]">NMI Number</td>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td className="border border-black p-[10px]">
                  {certificate.reportNo}
                </td>

                <td className="border border-black p-[10px]">
                  {certificate.make} {certificate.model}
                </td>

                <td className="border border-black p-[10px]">
                  {certificate.capacity}
                </td>

                <td className="border border-black p-[10px]">
                  {certificate.serialNumber}
                </td>

                <td className="border border-black p-[10px] w-[150px]">
                  {certificate.identifier || "\u00A0"}
                </td>

                <td className="border border-black p-[10px]">
                  {certificate.nmiNumber}
                </td>
              </tr>
            </tbody>
          </table>

          {/* LINE ITEMS */}
          <table className="w-full border border-black mb-4 text-center">
            <thead>
              <tr className="font-bold">
                <td className="border border-black p-[3px]">Load Applied</td>

                <td className="border border-black p-[3px]">
                  Before Calibration
                </td>

                <td className="border border-black p-[3px]">Error +/-</td>

                <td className="border border-black p-[3px]">
                  After Calibration
                </td>

                <td className="border border-black p-[3px]">Error +/-</td>
              </tr>
            </thead>

            <tbody>
              {Array.from({
                length: 5,
              }).map((_, index) => {
                const x = certificate.lineItems?.[index] || {};

                return (
                  <tr key={index}>
                    <td
                      className={`border border-black ${
                        x.loadApplied ? "p-[10px]" : "p-[14px]"
                      }`}
                    >
                      {x.loadApplied || ""}
                    </td>

                    <td
                      className={`border border-black ${
                        x.beforeCalibration ? "p-[10px]" : "p-[14px]"
                      }`}
                    >
                      {x.beforeCalibration || ""}
                    </td>

                    <td
                      className={`border border-black ${
                        x.beforeCalibrationError ? "p-[10px]" : "p-[14px]"
                      }`}
                    >
                      {x.beforeCalibrationError || ""}
                    </td>

                    <td
                      className={`border border-black ${
                        x.afterCalibration ? "p-[10px]" : "p-[14px]"
                      }`}
                    >
                      {x.afterCalibration || ""}
                    </td>

                    <td
                      className={`border border-black ${
                        x.afterCalibrationError ? "p-[10px]" : "p-[14px]"
                      }`}
                    >
                      {x.afterCalibrationError || ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* PASS FAIL */}
          <table className="w-full border border-black mb-4 text-center">
            <tbody>
              <tr className="font-bold">
                <td className="border border-black p-[3px]">
                  Before Calibration
                </td>

                <td className="border border-black p-[3px]">
                  After Calibration
                </td>
              </tr>

              <tr>
                <td className="border border-black p-[5px]">
                  {Number(certificate.beforeCalibration) === 0
                    ? "PASS"
                    : "FAIL"}
                </td>

                <td className="border border-black p-[5px]">
                  {Number(certificate.afterCalibration) === 0 ? "PASS" : "FAIL"}
                </td>
              </tr>
            </tbody>
          </table>

          {/* COMMENTS */}
          <table className="w-full border border-black mb-4">
            <tbody>
              <tr>
                <td className="border border-black p-[3px] font-bold w-[120px]">
                  Comments
                </td>

                <td className="border border-black p-[3px] h-[80px]">
                  {certificate.comments}
                </td>
              </tr>
            </tbody>
          </table>

          {/* FOOTER DETAILS */}
          <table className="w-full border border-black mb-4 text-center">
            <tbody>
              <tr className="font-bold">
                <td className="border border-black p-[4px]">Frequency(Months)</td>

                <td className="border border-black p-[4px]">
                  Maximum Permissible Error
                </td>

                <td className="border border-black p-[4px]">
                  Verification Code
                </td>
              </tr>

              <tr>
                <td className="border border-black p-[14px]">
                  {certificate.frequency}
                </td>

                <td className="border border-black p-[14px]">
                  {certificate.maximumPermissibleError}
                </td>

                <td className="border border-black p-[14px]">
                  {certificate.verificationCode &&
                  certificate.verificationCode.trim() !== ""
                    ? certificate.verificationCode
                    : "N/A"}
                </td>
              </tr>
            </tbody>
          </table>

          {/* CERTIFIED */}
          <table className="w-full border border-black mb-4 text-center">
            <tbody>
              <tr className="font-bold">
                <td className="border border-black p-[4px]">Scale Certified</td>

                <td className="border border-black p-[4px]">
                  Scale Used For Trade
                </td>

                <td className="border border-black p-[4px]">
                  Verification Date
                </td>
              </tr>

              <tr>
                <td className="border border-black p-[14px]">
                  {Number(certificate.scaleCertified) === 0 ? "YES" : "NO"}
                </td>

                <td className="border border-black p-[14px]">
                  {Number(certificate.scaleUsedForTrade) === 0 ? "YES" : "NO"}
                </td>

                <td className="border border-black p-[14px]">
                  {certificate.verificationDate
                    ? new Date(certificate.verificationDate).toLocaleDateString(
                        "en-GB",
                      )
                    : "N/A"}
                </td>
              </tr>
            </tbody>
          </table>

          {/* FINAL */}
          <table className="w-full border border-black mb-3">
            <tbody>
              <tr>
                <td className="border border-black p-[10px] w-1/3">
                  <b>Calibrated By:</b> {certificate.calibratedBy}
                </td>

                <td className="border border-black p-[10px] w-1/3">
                  <b>Date:</b>{" "}
                  {certificate.date
                    ? new Date(certificate.date).toLocaleDateString("en-GB")
                    : ""}
                </td>

                <td className="border border-black p-[10px] w-1/3">
                  <b>Date Due:</b>{" "}
                  {certificate.dueDate
                    ? new Date(certificate.dueDate).toLocaleDateString("en-GB")
                    : ""}
                </td>
              </tr>
            </tbody>
          </table>

          {/* FOOTER */}
          <ReportFooter
            message="All tests for trade scales carried out in accordance with NMI R76
              and test weights used for calibration are checked and verified
              under NMI Regulation 13."
          />
        </div>
      </div>
      {showEditModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-[95vw] h-[95vh] rounded-2xl flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-2xl font-bold">
                Edit Calibration Certificate
              </h2>

              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-xl bg-red-500 text-white"
              >
                Close
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              <EditCalibrationCertificateForm
                id={id!}
                onClose={() => setShowEditModal(false)}
                onSaved={() => {
                  setShowEditModal(false);
                  loadData();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ViewCalibrationCertificate;
